import { useState } from "react";
import formatDate from "../../util/dateFormat";
import axios from "axios";
import { APIConstants, APIModuleConstants } from "../../constants/ApiConstants";
import { useAppDispatch } from "../../redux/hooks";
import { loyaltyPointsActions } from "../../redux/slices/UpdateLoyaltyPoints";
import { generateExpiryDateOptions } from "../../components/forms/utility";
import useGetTotalRecords from "../CustomHooks/UseGetTotalRecords";
import { emailMasking } from "../../util/MaskingUtils";
import { constant } from "../../constants";

const useGetLoyaltyPoints = () => {
  const dispatch = useAppDispatch();
  const [loadingGetPoints, setLoadingGetPoints] = useState<boolean>(false);
  const [transactionTypeList, setTransactionTypeList] = useState<any>([]);
  const [loadingAddPoints, setLoadingAddPoints] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [data, setData] = useState([]);
  const [lastKey, setLastKey] = useState<string>("");
  const [transactionTime, setTransactionTime] = useState<any>(null);
  const [uploadError, setUploadError] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [clearForm, setClearForm] = useState(false);
  const { totalCount, getTotalRecordsCount } = useGetTotalRecords();

  const convertDisplayToValue = (displayValue: any) => {
    const matchingOption = generateExpiryDateOptions().find(
      (option) => option.display === displayValue
    );

    return matchingOption ? matchingOption.value : null;
  };

  const getPointsBySplit = (points: string) => {
    try {
      // Check if points contains a space
      if (points?.includes(" ")) {
        const pointsArray = points?.split(" ");
        const integerPart = +pointsArray[1];
        const roundOff = Math.round(integerPart);
        return `${pointsArray[0]} ${roundOff}`;
      } else {
        // If no space, return the points directly
        return points;
      }
    } catch (error) {
      return points;
    }
  };
  const getLoyaltyPoints = async (
    urlString?: string,
    canHaveExistingData?: boolean
  ) => {
    setLoadingGetPoints(true);

    let currentURl = `${process.env.REACT_APP_API_URL}${APIConstants.LoyaltyPoints}`;
    if (urlString) currentURl += `?${urlString}`;

    try {
      const response = await axios.get(currentURl);

      const key = response?.data?.result?.data?.lastKey?.email;
      setLastKey(key || null);
      const time = response?.data?.result?.data?.lastKey?.transaction_time;
      setTransactionTime(time || null);
      const valuesForMap = response.data.result.data.value;

      const mappedData = valuesForMap.map((item: any, index: any) => ({
        transactionDate: formatDate(item.createdAt),
        status: item.status && item.status,
        transactionType: item.transaction_type && item.transaction_type,
        points: getPointsBySplit(item.loyalty_points),
        // points: item.loyalty_points,
        // pointsBalance: item.points_balance,
        pointsBalance: Math.round(item?.points_balance),
        pointsExpiry: item.points_expiry ? formatDate(item.points_expiry) : "-",
        remark: item.remarks,
        merchantName: item.campaign ? item.campaign : "-",
        createdBy:
          constant.domain === process.env.REACT_APP_CSA_DOMAIN
            ? emailMasking(item.created_by || "-")
            : item.created_by || "-",
        submittedBy: item.submitted_by,
        campaignName: item.campaign_name && item.campaign_name,
      }));

      // Sort the data based on transaction date
      mappedData.sort(
        (
          a: { transactionDate: string | number | Date },
          b: { transactionDate: string | number | Date }
        ) =>
          new Date(b.transactionDate).getTime() -
          new Date(a.transactionDate).getTime()
      );
      let completeData = canHaveExistingData
        ? [...data, ...mappedData]
        : mappedData;
      setData(completeData);

      if (!transactionTypeList.length) {
        const uniqueTransactionType = Array.from(
          new Set(valuesForMap.map((tr: any) => tr.transaction_type))
        );
        setTransactionTypeList(uniqueTransactionType);
      }
      getTotalRecordsCount(APIModuleConstants.memberPoints, urlString);
      setError(false);
      setLoadingGetPoints(false);
    } catch (error) {
      setLoadingGetPoints(false);
      setError(true);
    }
  };
  const getCustomer = async (memberDetails: any) => {
    try {
      const details = await axios.get(
        process.env.REACT_APP_API_URL +
          `${APIConstants.getCustomer}?email=${memberDetails.email}`
      );
      const mappedData = details.data.data.data;
      dispatch(
        loyaltyPointsActions.updatePoints({
          points: mappedData.loyaltyPoints,
        })
      );
    } catch (error) {
    } finally {
    }
  };
  const addLoyaltyPoints = async (
    loyaltyPointFormState: any,
    memberDetails: any
  ) => {
    setLoadingAddPoints(true);
    try {
      const payload: any = loyaltyPointFormState;
      payload.email = memberDetails.email;
      payload.points = +payload.points;

      payload.expiry = convertDisplayToValue(payload.expiry);
      if (loyaltyPointFormState?.transaction_type === "Add Points") {
        const axiosResponse = await axios.post(
          process.env.REACT_APP_API_URL + APIConstants.LoyaltyPoints,
          payload
        );
        const points = axiosResponse.data.result.points_balance;

        dispatch(
          loyaltyPointsActions.updatePoints({
            points: points,
          })
        );
      }
      if (loyaltyPointFormState?.transaction_type === "Deduct Points") {
        const axiosResponse = await axios.patch(
          process.env.REACT_APP_API_URL + APIConstants.LoyaltyPoints,
          payload
        );
        const points = axiosResponse.data.result.points_balance;
        dispatch(
          loyaltyPointsActions.updatePoints({
            points: points,
          })
        );
      }

      getLoyaltyPoints(`email=${memberDetails.email}`);
      setUploadError(false);
    } catch (error) {
      setLoadingAddPoints(false);
      setUploadError(true);
    } finally {
      setLoadingAddPoints(false);
      setShowModal(true);
      setClearForm(true);
      setTimeout(() => {
        setClearForm(false);
      }, 2000);
    }
  };
  return {
    getLoyaltyPoints,
    loadingGetPoints,
    loadingAddPoints,
    data,
    uploadError,
    showModal,
    clearForm,
    addLoyaltyPoints,
    transactionTypeList,
    totalCount,
    getCustomer,
    lastKey,
    transactionTime,
  };
};

export default useGetLoyaltyPoints;
